/**
 * Sorting, filtering and totals for the clearance table — the clearance twin
 * of `@/lib/stationVolumes/tableModel`.
 *
 * The click / shift-click rules (`nextSort`, `isDefaultSort`), the Search STA
 * parser and the bin-contains-station test are SHARED with station volumes,
 * not copied: the two tables sit in the same dock slot and a reader who
 * learned one must not find the other sorting differently. Only what is
 * genuinely clearance-shaped lives here — the columns a `ClearanceBin` has,
 * the per-bin verdict, and what "a total" of a clearance column means.
 */
import { binVerdict, type Verdict } from '@gis/clearance';

import { stationMatchesBin } from '@/lib/stationVolumes/tableModel';

import type { ClearanceBin, ClearanceNearMiss } from './types';

/** The bin's own verdict — shared with the report worker, so it lives in
 *  `@gis/clearance` (`verdict.ts`); re-exported so table call sites keep
 *  importing it from here. */
export { binVerdict };

export type ClearanceSortKey =
  | 'station'
  | 'verdict'
  | 'points'
  | 'violations'
  | 'maxIntrusion'
  | 'worstOffset'
  | 'worstHeight'
  | 'near01'
  | 'near02'
  | 'near035';

export interface ClearanceSort {
  key: ClearanceSortKey;
  /** 1 ascending, -1 descending. */
  dir: 1 | -1;
}

export const CLEARANCE_DEFAULT_SORT: ClearanceSort[] = [{ key: 'station', dir: 1 }];

/**
 * Worst first when sorted descending. INCONCLUSIVE has no rank: a bin the
 * sweep scored nothing in has no verdict to compare, and it sorts LAST in
 * both directions — the same "missing figures last" rule as every other
 * column, because an empty bin is not a passing one.
 */
const VERDICT_RANK: Record<Verdict, number | null> = { FAIL: 3, MARGINAL: 2, PASS: 1, INCONCLUSIVE: null };

/** The figure each column sorts on — the figure the cell PRINTS, so the order
 *  the reader asked for is the order they can see. `null` sorts last. */
const SORT_VALUE: Record<ClearanceSortKey, (b: ClearanceBin) => number | null> = {
  station: (b) => b.staStartInternalM,
  verdict: (b) => VERDICT_RANK[binVerdict(b)],
  points: (b) => b.pointCount,
  violations: (b) => b.violationCount,
  maxIntrusion: (b) => b.maxIntrusionM,
  // Signed, as printed: the offset is a POSITION (left − / right +), not a
  // magnitude, so sorting it groups the left-hand violations from the right.
  worstOffset: (b) => b.worstOffsetM,
  worstHeight: (b) => b.worstHeightM,
  near01: (b) => b.nearMiss.within0_1M,
  near02: (b) => b.nearMiss.within0_2M,
  near035: (b) => b.nearMiss.within0_35M,
};

/** Sorted copy. Ties fall through to the next sort, then to bin order, so the
 *  same data always lands in the same order. */
export function sortClearanceBins(bins: readonly ClearanceBin[], sorts: readonly ClearanceSort[]): ClearanceBin[] {
  const rules = sorts.length > 0 ? sorts : CLEARANCE_DEFAULT_SORT;
  return [...bins].sort((a, b) => {
    for (const { key, dir } of rules) {
      const va = SORT_VALUE[key](a);
      const vb = SORT_VALUE[key](b);
      if (va == null || vb == null) {
        if (va == null && vb == null) continue;
        return va == null ? 1 : -1;
      }
      if (va !== vb) return (va - vb) * dir;
    }
    return a.i - b.i;
  });
}

export interface ClearanceFilter {
  /** A parsed Search STA (`parseStationQuery`), or null for no station filter. */
  station: number | null;
  onlyViolations: boolean;
  /** Inclusive bin range of the brush, or null for no selection filter. */
  selection: { lo: number; hi: number } | null;
}

export function filterClearanceBins(bins: readonly ClearanceBin[], f: ClearanceFilter): ClearanceBin[] {
  return bins.filter(
    (b) =>
      (!f.selection || (b.i >= f.selection.lo && b.i <= f.selection.hi)) &&
      (!f.onlyViolations || b.violationCount > 0) &&
      (f.station == null || stationMatchesBin(b, f.station)),
  );
}

export interface ClearanceTotals {
  bins: number;
  points: number;
  violations: number;
  /** The deepest intrusion among the rows, null when none intruded. */
  maxIntrusionM: number | null;
  /** Where THAT intrusion is — the offset and height of the deepest row, not
   *  a sum or a mean, which would be a position nobody measured. */
  worstOffsetM: number | null;
  worstHeightM: number | null;
  /** Summed per band. Each band is nested within a bin, so the sums stay
   *  nested too (`≤ 0.1 ≤ ≤ 0.2 ≤ ≤ 0.35`). */
  nearMiss: ClearanceNearMiss;
}

/**
 * Totals over exactly the bins handed in — the caller decides the scope.
 *
 * Deliberately WITHOUT a verdict. A verdict is a judgement of the run, made
 * once from its summary (`useClearanceVerdict`); re-deriving one for a
 * filtered sub-range would put a second, disagreeing "Pass" in the footer of
 * a run whose header says "Fail".
 */
export function clearanceTotals(bins: readonly ClearanceBin[]): ClearanceTotals {
  let points = 0;
  let violations = 0;
  let worst: ClearanceBin | null = null;
  const nearMiss: ClearanceNearMiss = { within0_1M: 0, within0_2M: 0, within0_35M: 0 };
  for (const b of bins) {
    points += b.pointCount;
    violations += b.violationCount;
    nearMiss.within0_1M += b.nearMiss.within0_1M;
    nearMiss.within0_2M += b.nearMiss.within0_2M;
    nearMiss.within0_35M += b.nearMiss.within0_35M;
    if (b.maxIntrusionM != null && (worst?.maxIntrusionM == null || b.maxIntrusionM > worst.maxIntrusionM)) worst = b;
  }
  return {
    bins: bins.length,
    points,
    violations,
    maxIntrusionM: worst?.maxIntrusionM ?? null,
    worstOffsetM: worst?.worstOffsetM ?? null,
    worstHeightM: worst?.worstHeightM ?? null,
    nearMiss,
  };
}

/** The bin an INTERNAL walk station stands in. Half-open, except that the
 *  corridor's very last station belongs to the last bin. */
export function binAtStation(bins: readonly ClearanceBin[], station: number): number | null {
  const hit = bins.find(
    (b, idx) =>
      station >= b.staStartInternalM &&
      (station < b.staEndInternalM || (idx === bins.length - 1 && station <= b.staEndInternalM)),
  );
  return hit ? hit.i : null;
}
